/**
 * TrainerSearchSelect
 *
 * A searchable trainer combobox built on Radix Popover so it works correctly
 * inside Radix Dialog without DismissableLayer blocking clicks.
 *
 * Fetches trainers from GET /trainers?search=...&isActive=true&limit=10
 *   • On open  — loads first 10 active trainers immediately
 *   • On type  — debounces 300 ms, then fetches matching trainers
 *   • Searches — by name, email, or phone (mobile)
 *
 * Props:
 *   value       – currently selected trainer _id (string)
 *   onChange    – (trainerId: string) => void
 *   placeholder – string (default "Select a trainer")
 *   disabled    – boolean
 */

import * as Popover from '@radix-ui/react-popover';
import { ChevronDown, Search, X, UserCircle } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';

const TrainerSearchSelect = ({
  value,
  onChange,
  placeholder = 'Select a trainer',
  disabled = false,
}) => {
  const [open, setOpen]           = useState(false);
  const [search, setSearch]       = useState('');
  const [trainers, setTrainers]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [selected, setSelected]   = useState(null); // full trainer object for display
  const debounceRef               = useRef(null);
  const inputRef                  = useRef(null);

  // Fetch trainers from the API
  const fetchTrainers = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const params = { isActive: 'true', limit: 10 };
      if (q.trim()) params.search = q.trim();
      const res = await api.get('/trainers', { params });
      setTrainers(res.data?.trainers || []);
    } catch {
      setTrainers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // When the popover opens, load default list
  const handleOpenChange = (next) => {
    if (disabled) return;
    setOpen(next);
    if (next) {
      setSearch('');
      fetchTrainers('');
      // Auto-focus search input after render
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  // Debounce search input
  useEffect(() => {
    if (!open) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchTrainers(search), 300);
    return () => clearTimeout(debounceRef.current);
  }, [search, open, fetchTrainers]);

  // When value changes externally (or on initial mount), fetch the trainer's name for display
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    // If already in the current list, use it
    const found = trainers.find(t => t._id === value);
    if (found) { setSelected(found); return; }
    // Otherwise fetch by ID
    api.get(`/trainers/${value}`)
      .then(res => setSelected(res.data?.trainer || res.data || null))
      .catch(() => setSelected(null));
  }, [value]); // trainers intentionally omitted — we only re-fetch by ID when value changes

  const handleSelect = (trainer) => {
    setSelected(trainer);
    onChange(trainer._id);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelected(null);
    onChange('');
  };

  const displayLabel = selected
    ? `${selected.name}${selected.expertise?.length ? ' — ' + selected.expertise.slice(0, 2).join(', ') : ''}`
    : null;

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          className="w-full h-9 px-3 text-sm flex items-center justify-between rounded-md border border-input bg-background hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className={`truncate ${selected ? 'text-foreground' : 'text-muted-foreground'}`}>
            {displayLabel ?? placeholder}
          </span>
          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
            {value && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={handleClear}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </span>
            )}
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
          </div>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="z-[9999] bg-white border border-gray-200 rounded-md shadow-lg outline-none overflow-hidden"
          style={{
            width: 'var(--radix-popover-trigger-width)',
            minWidth: '260px',
            maxWidth: 'min(var(--radix-popover-trigger-width), 90vw)',
          }}
          sideOffset={4}
          align="start"
          avoidCollisions
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-1.5 border border-gray-200 rounded-md px-2 py-1 bg-gray-50 focus-within:border-emerald-400 focus-within:bg-white transition-colors">
              <Search className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name, email or mobile…"
                className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder-gray-400"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>

          {/* Results list */}
          <ul className="max-h-64 overflow-y-auto py-1 popup-scroll">
            {loading ? (
              <li className="py-6 text-center text-xs text-gray-400">Loading trainers…</li>
            ) : trainers.length === 0 ? (
              <li className="py-6 text-center text-xs text-gray-500">
                {search ? `No trainers match "${search}"` : 'No active trainers found'}
              </li>
            ) : (
              <>
                <li className="px-3 py-1 text-[10px] text-gray-400 font-medium uppercase tracking-wide border-b border-gray-100 bg-gray-50/60">
                  {search ? `Results for "${search}"` : `Showing ${trainers.length} active trainers`}
                </li>
                {trainers.map(t => {
                  const isSelected = t._id === value;
                  const expertise  = t.expertise?.slice(0, 2).join(', ');
                  return (
                    <li key={t._id}>
                      <button
                        type="button"
                        onClick={() => handleSelect(t)}
                        className={[
                          'w-full text-left px-3 py-2 text-sm flex items-center gap-2.5 transition-colors',
                          isSelected ? 'bg-emerald-50 text-emerald-800' : 'hover:bg-gray-50 text-gray-800',
                        ].join(' ')}
                      >
                        <UserCircle className={`h-5 w-5 flex-shrink-0 ${isSelected ? 'text-emerald-500' : 'text-gray-300'}`} />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate text-sm">{t.name}</div>
                          {expertise && (
                            <div className="text-xs text-gray-400 truncate mt-0.5">{expertise}</div>
                          )}
                          {t.phone && (
                            <div className="text-xs text-gray-400 mt-0.5">{t.phone}</div>
                          )}
                        </div>
                        {isSelected && (
                          <span className="text-emerald-500 text-xs font-semibold flex-shrink-0">✓</span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </>
            )}
          </ul>

          {/* Footer hint */}
          <div className="px-3 py-1.5 border-t border-gray-100 bg-gray-50/60">
            <p className="text-[10px] text-gray-400">Type to search — shows top 10 results</p>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default TrainerSearchSelect;
